import React, { useMemo, useState } from 'react';
import {
  IonBadge,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonButton,
  IonSearchbar,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  IonSegment,
  IonSegmentButton,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useProcessedMaterials } from '../hooks/useManufacturing';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { formatCurrency, formatDecimal } from '../utils/format';
import MetricCard from '../components/MetricCard';

const ProcessedMaterials: React.FC = () => {
  const history = useHistory();
  const { t, language } = useLanguage();
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';
  const {
    processedMaterials,
    processedMaterialsLoading,
    manufacturingHistory,
    manufacturingHistoryLoading,
  } = useProcessedMaterials();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'low' | 'ok'>('all');

  const filtered = processedMaterials
    .filter((material) => material.name.toLowerCase().includes(search.toLowerCase()))
    .filter((material) => {
      if (statusFilter === 'all') {
        return true;
      }
      const currentStock = Number(material.currentStock || 0);
      const minStock = Number(material.minStockLevel || 0);
      const isLow = currentStock < minStock;
      return statusFilter === 'low' ? isLow : !isLow;
    });

  const metrics = useMemo(() => {
    const totalRecipes = processedMaterials.length;
    const lowStockCount = processedMaterials.reduce((count, material) => {
      const currentStock = Number(material.currentStock || 0);
      const minStock = Number(material.minStockLevel || 0);
      return currentStock < minStock ? count + 1 : count;
    }, 0);
    const totalStockValue = processedMaterials.reduce((sum, material) => {
      const currentStock = Number(material.currentStock || 0);
      const cost = Number(material.costPerUnit || 0);
      return sum + currentStock * cost;
    }, 0);
    const batchesLast30Days = manufacturingHistory.reduce((total, entry) => {
      if (!entry?.manufacturedAt) {
        return total;
      }
      const date = new Date(entry.manufacturedAt);
      if (Number.isNaN(date.getTime())) {
        return total;
      }
      const diffDays = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
      if (diffDays <= 30) {
        return total + Number(entry.quantityProduced || 0);
      }
      return total;
    }, 0);
    const totalBatches = manufacturingHistory.length;

    return {
      totalRecipes,
      lowStockCount,
      totalStockValue,
      batchesLast30Days,
      totalBatches,
    };
  }, [processedMaterials, manufacturingHistory]);

  const metricsLoading = processedMaterialsLoading || manufacturingHistoryLoading;

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('manufactured')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <IonButton
          expand="block"
          style={{ marginBottom: 16 }}
          onClick={() => history.push('/tabs/manufacturing/new')}
        >
          {t('addProcessedMaterial') ?? 'Add processed material'}
        </IonButton>

        {metricsLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <IonSpinner />
          </div>
        ) : (
          <section style={{ marginBottom: 16 }}>
            <MetricCard
              title={t('totalRecipes') ?? 'Total recipes'}
              value={`${metrics.totalRecipes}`}
              context={`${metrics.totalBatches} ${t('batchesRecorded') ?? 'Batches recorded'}`}
              accent="primary"
            />
            <MetricCard
              title={t('stockValue') ?? 'Stock value'}
              value={formatCurrency(metrics.totalStockValue, locale)}
              context={`${t('lowStockCount') ?? 'Low stock items'}: ${metrics.lowStockCount}`}
              accent="warning"
            />
            <MetricCard
              title={t('productionLast30Days') ?? 'Batches (30d)'}
              value={formatDecimal(metrics.batchesLast30Days, { locale })}
              context={t('quantityProduced') ?? 'Quantity produced'}
              accent="success"
            />
          </section>
        )}

        <IonSearchbar
          value={search}
          onIonChange={(event) => setSearch(event.detail.value ?? '')}
          placeholder={t('searchProducts') ?? 'Search processed materials'}
        />
        <IonSegment
          value={statusFilter}
          onIonChange={(event) => {
            const value = (event.detail.value as 'all' | 'low' | 'ok' | undefined) ?? 'all';
            setStatusFilter(value);
          }}
          style={{ marginBottom: 16 }}
        >
          <IonSegmentButton value="all">
            <IonLabel>{t('all') ?? 'All'}</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="low">
            <IonLabel>{t('lowStock') ?? 'Low Stock'}</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="ok">
            <IonLabel>{t('inStock') ?? 'In Stock'}</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        {processedMaterialsLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <IonSpinner />
          </div>
        ) : filtered.length === 0 ? (
          <IonText color="medium">
            <p>{t('noData')}</p>
          </IonText>
        ) : (
          <IonList>
            {filtered.map((material) => {
              const currentStock = Number(material.currentStock || 0);
              const minStock = Number(material.minStockLevel || 0);
              const isLow = currentStock < minStock;

              return (
                <IonItem
                  key={material.id}
                  button
                  detail
                  onClick={() => history.push(`/tabs/manufacturing/${material.id}`)}
                >
                  <IonLabel>
                    <h2 style={{ fontWeight: 600 }}>{material.name}</h2>
                    <p>
                      {t('currentStock')}: {formatDecimal(currentStock, { locale, maximumFractionDigits: 6 })}{' '}
                      {material.unit}
                    </p>
                    <p>
                      {t('minStockLevel')}: {formatDecimal(minStock, { locale, maximumFractionDigits: 6 })}{' '}
                      {material.unit}
                    </p>
                    <p>
                      {t('costPerUnit')}: {formatCurrency(Number(material.costPerUnit || 0), locale)}
                    </p>
                  </IonLabel>
                  <IonBadge slot="end" color={isLow ? 'warning' : 'success'}>
                    {isLow ? t('lowStock') : t('inStock')}
                  </IonBadge>
                </IonItem>
              );
            })}
          </IonList>
        )}
      </IonContent>
    </IonPage>
  );
};

export default ProcessedMaterials;
